import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Filter, RotateCcw, Search } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ExportMenu } from "../components/ExportMenu";
import { inputClass, selectClass } from "../components/FormField";
import { fetchScopeCompanies, type ScopeCompany } from "../api/insurerApi";
import { fetchClaimsReport, fetchInsurerPackages, type ClaimsReport, type ClaimsReportFilters, type ReportStats } from "../api/claimsApi";
import { exportCsv } from "@/shared/lib/exportCsv";

type Row = ReportStats & { label: string; code?: string | null; cases?: number; beneficiaries?: number };

type Kind = {
  title: string;
  searchLabel: string;
  rows: (report: ClaimsReport) => Row[];
  /** Colonnes propres à la liste, avant les montants. */
  columns: Array<{ label: string; value: (row: Row) => string | number; numeric?: boolean }>;
};

const KINDS: Record<string, Kind> = {
  nature: {
    title: "Natures d'acte",
    searchLabel: "Rechercher une nature d'acte…",
    rows: (report) => report.by_nature,
    columns: [{ label: "Nature d'acte", value: (row) => row.label }],
  },
  company: {
    title: "Consommation par entreprise",
    searchLabel: "Rechercher une entreprise…",
    rows: (report) => report.by_company,
    columns: [
      { label: "Entreprise", value: (row) => row.label },
      { label: "Bénéficiaires", value: (row) => row.beneficiaries ?? 0, numeric: true },
    ],
  },
  provider: {
    title: "Prestataires",
    searchLabel: "Rechercher un prestataire…",
    rows: (report) => report.by_provider,
    columns: [
      { label: "Prestataire", value: (row) => row.label },
      { label: "Bénéficiaires", value: (row) => row.beneficiaries ?? 0, numeric: true },
    ],
  },
  pathology: {
    title: "Affections",
    searchLabel: "Rechercher une affection…",
    rows: (report) => report.by_pathology,
    columns: [
      { label: "Affection", value: (row) => row.label },
      { label: "Cas", value: (row) => row.cases ?? 0, numeric: true },
    ],
  },
};

const AMOUNTS: Array<{ label: string; value: (row: Row) => number; format: (value: number) => string }> = [
  { label: "Actes", value: (row) => row.acts, format: (value) => value.toLocaleString("fr-FR") },
  { label: "Facturé", value: (row) => row.requested, format: (value) => money(value) },
  { label: "Validé", value: (row) => row.validated, format: (value) => money(value) },
  { label: "Moyenne par acte", value: (row) => row.average, format: (value) => money(value) },
  { label: "Part", value: (row) => row.share, format: (value) => `${value.toLocaleString("fr-FR")} %` },
];

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const NO_FILTERS: ClaimsReportFilters = { package: "", provider: "", nature: "" };

/** Compte Assurance : liste complète et filtrable derrière une carte du tableau de bord. */
export function InsurerAnalysisPage() {
  const { kind: kindKey = "" } = useParams();
  const kind = KINDS[kindKey];

  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [filters, setFilters] = useState<ClaimsReportFilters>(NO_FILTERS);
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState<ScopeCompany[]>([]);
  const [packages, setPackages] = useState<string[]>([]);
  const [base, setBase] = useState<ClaimsReport | null>(null);
  const [report, setReport] = useState<ClaimsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchScopeCompanies().then(setCompanies).catch(() => setCompanies([]));
    fetchInsurerPackages().then((rows) => setPackages(rows.map((row) => row.name))).catch(() => setPackages([]));
  }, []);

  const filtered = Boolean(filters.package || filters.provider || filters.nature);

  useEffect(() => {
    if (from > to) return;
    let active = true;
    setLoading(true);
    setError(null);
    // Le rapport sans filtre donne la liste complète des prestataires et natures pour les menus.
    Promise.all([fetchClaimsReport(from, to, companyId ?? undefined), filtered ? fetchClaimsReport(from, to, companyId ?? undefined, filters) : null])
      .then(([all, selection]) => {
        if (!active) return;
        setBase(all);
        setReport(selection ?? all);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Chargement impossible."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [from, to, companyId, filters, filtered]);

  const rows = useMemo(() => {
    if (!report || !kind) return [];
    const term = search.trim().toLowerCase();
    return kind.rows(report).filter((row) => !term || row.label.toLowerCase().includes(term));
  }, [report, kind, search]);

  if (!kind) return <Navigate to="/dashboard/enterprise" replace />;

  const setFilter = (key: keyof ClaimsReportFilters, value: string) => setFilters((current) => ({ ...current, [key]: value }));
  const reset = () => {
    setFilters(NO_FILTERS);
    setCompanyId(null);
    setSearch("");
    setFrom(`${year}-01-01`);
    setTo(new Date().toISOString().slice(0, 10));
  };

  const handleExport = () =>
    exportCsv(
      `${kindKey}-${from}-${to}.csv`,
      [...kind.columns.map((column) => column.label), ...AMOUNTS.map((column) => column.label)],
      rows.map((row) => [...kind.columns.map((column) => column.value(row)), ...AMOUNTS.map((column) => column.value(row))]),
    );

  const totals = rows.reduce((sum, row) => ({ acts: sum.acts + row.acts, requested: sum.requested + row.requested, validated: sum.validated + row.validated }), { acts: 0, requested: 0, validated: 0 });

  const select = (label: string, value: string, onChange: (value: string) => void, all: string, options: Array<{ value: string; label: string }>) => (
    <label className="flex min-w-[10rem] flex-1 flex-col gap-1 text-xs text-muted">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={`${selectClass} h-9 text-sm`}>
        <option value="">{all}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs homeHref="/dashboard/enterprise" items={[{ label: "Tableau de bord", href: "/dashboard/enterprise" }, { label: kind.title }]} />
        <Link to="/dashboard/enterprise" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground hover:border-accent hover:bg-accent/20">
          <ArrowLeft size={15} aria-hidden="true" />
          Retour au tableau de bord
        </Link>
      </div>

      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}

      <div className="theme-card">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">{kind.title}</h1>
            <p className="text-sm text-muted">
              {loading ? "Chargement…" : `${rows.length} ligne(s) · ${totals.acts.toLocaleString("fr-FR")} acte(s) · facturé ${money(totals.requested)} · validé ${money(totals.validated)}`}
            </p>
          </div>
          <ExportMenu onExport={handleExport} />
        </div>

        <div className="flex flex-wrap items-end gap-3 border-b border-border px-5 py-4">
          <span className="flex items-center gap-2 self-center text-sm font-semibold text-foreground">
            <Filter size={16} className="text-primary" aria-hidden="true" />
            Filtres
          </span>
          <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-muted">
            Du
            <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} className={`${inputClass} h-9 text-sm`} />
          </label>
          <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-muted">
            Au
            <input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} className={`${inputClass} h-9 text-sm`} />
          </label>
          {select("Entreprise", companyId ? String(companyId) : "", (value) => setCompanyId(value ? Number(value) : null), "Toutes les entreprises", companies.map((company) => ({ value: String(company.id), label: company.name })))}
          {select("Package", filters.package, (value) => setFilter("package", value), "Tous les packages", packages.map((name) => ({ value: name, label: name })))}
          {kindKey !== "provider"
            ? select("Prestataire", filters.provider, (value) => setFilter("provider", value), "Tous les prestataires", (base?.by_provider ?? []).map((row) => ({ value: row.label, label: row.label })))
            : null}
          {kindKey !== "nature"
            ? select("Nature d'acte", filters.nature, (value) => setFilter("nature", value), "Toutes les natures", (base?.by_nature ?? []).map((row) => ({ value: row.key, label: row.label })))
            : null}
          <button type="button" onClick={reset} className="inline-flex h-9 items-center gap-1 rounded-lg border border-border px-3 text-sm text-foreground hover:bg-card-muted">
            <RotateCcw size={14} aria-hidden="true" />
            Réinitialiser
          </button>
        </div>

        <div className="border-b border-border px-5 py-3">
          <label className="relative block max-w-md">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={kind.searchLabel} aria-label={kind.searchLabel} className={`${inputClass} pl-9`} />
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-card-muted text-left text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-2">#</th>
                {kind.columns.map((column) => (
                  <th key={column.label} className={`px-4 py-2 ${column.numeric ? "text-right" : ""}`}>{column.label}</th>
                ))}
                {AMOUNTS.map((column) => (
                  <th key={column.label} className="px-4 py-2 text-right">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={kind.columns.length + AMOUNTS.length + 1} className="px-4 py-10 text-center text-muted">
                    {loading ? "Chargement…" : "Aucune donnée pour ces filtres."}
                  </td>
                </tr>
              ) : null}
              {rows.map((row, index) => (
                <tr key={`${row.code ?? ""}-${row.label}`} className="border-t border-border">
                  <td className="px-4 py-2 tabular-nums text-muted">{index + 1}</td>
                  {kind.columns.map((column) => (
                    <td key={column.label} className={`px-4 py-2 text-foreground ${column.numeric ? "text-right tabular-nums" : ""}`}>{column.value(row)}</td>
                  ))}
                  {AMOUNTS.map((column) => (
                    <td key={column.label} className="px-4 py-2 text-right tabular-nums text-foreground">{column.format(column.value(row))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
